import React, { useState } from 'react';
import { 
  Search, Filter, MoreVertical, Mail, Phone, ShoppingBag, 
  ShieldCheck, UserX, Eye, MessageSquare, Download, TrendingUp, 
  Users, UserCheck, UserMinus, DollarSign, Calendar, Star,
  ChevronLeft, ChevronRight, Edit, Trash2, Award, X,
  MapPin, Clock, CheckCircle, AlertCircle, CreditCard, Package,
  User
} from 'lucide-react';

import { useDispatch, useSelector } from 'react-redux';
import { fetchUsers, deleteUser } from '../../redux/adminUsersSlice';

const Customers = () => {
  const dispatch = useDispatch();
  const { users, loading, error } = useSelector((state) => state.adminUsers);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterSort, setFilterSort] = useState('newest');
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const itemsPerPage = 6;

  React.useEffect(() => {
    dispatch(fetchUsers());
  }, [dispatch]);

  // Statistics
  const stats = {
    total: users.length,
    active: users.filter(c => c.isActive !== false).length,
    vip: 0,
    inactive: users.filter(c => c.isActive === false).length,
    totalSpent: 0,
    totalOrders: 0
  };

  const getStatusConfig = (status) => {
    switch(status) {
      case 'VIP': 
        return { label: 'VIP', icon: Award, color: '#8b5cf6', bg: '#e9d5ff' };
      case 'Active': 
        return { label: 'Active', icon: UserCheck, color: '#10b981', bg: '#d1fae5' };
      default: 
        return { label: 'Inactive', icon: UserMinus, color: '#ef4444', bg: '#fee2e2' };
    }
  };

  const handleViewDetails = (customer) => {
    setSelectedCustomer(customer);
    setShowDetailsModal(true);
  };

  const handleDeleteUser = (id) => {
    if (window.confirm("Are you sure you want to delete this user?")) {
      dispatch(deleteUser(id));
    }
  };

  const filteredCustomers = users.filter(customer => {
    const matchesSearch = customer.username?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                          customer.email.toLowerCase().includes(searchTerm.toLowerCase());
    const cStatus = customer.isActive === false ? 'Inactive' : 'Active';
    const matchesStatus = filterStatus === 'all' || cStatus === filterStatus;
    return matchesSearch && matchesStatus;
  });

  // Sort customers
  const sortedCustomers = [...filteredCustomers].sort((a, b) => {
    if (filterSort === 'newest') return new Date(b.createdAt) - new Date(a.createdAt);
    if (filterSort === 'oldest') return new Date(a.createdAt) - new Date(b.createdAt);
    return 0;
  });

  const totalPages = Math.ceil(sortedCustomers.length / itemsPerPage);
  const paginatedCustomers = sortedCustomers.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);

  const StatCard = ({ title, value, icon: Icon, color, bgColor }) => (
    <div className="stat-card">
      <div className="stat-icon" style={{ backgroundColor: bgColor }}>
        <Icon size={20} color={color} />
      </div>
      <div className="stat-info">
        <h3>{typeof value === 'number' ? value.toLocaleString() : value}</h3>
        <span>{title}</span>
      </div>
    </div>
  );

  const DetailSection = ({ title, icon: Icon, children }) => (
    <div className="detail-section">
      <div className="detail-section-header">
        <Icon size={16} />
        <h4>{title}</h4>
      </div>
      <div className="detail-section-content">
        {children}
      </div>
    </div>
  );

  if (loading) return <div className="p-4">Loading customers...</div>;
  if (error) return <div className="p-4 text-danger">{error}</div>;

  return (
    <div className="admin-customers-modern">
      {/* Header */}
      <div className="customers-header">
        <div>
          <h1 className="page-title">Customers</h1>
          <p className="page-subtitle">Manage registered users and their purchasing history</p>
        </div>
        <div className="header-actions">
          <button className="btn-export">
            <Download size={16} />
            Export List
          </button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="stats-grid">
        <StatCard title="Total Customers" value={stats.total} icon={Users} color="#10b981" bgColor="#d1fae5" />
        <StatCard title="Active" value={stats.active} icon={UserCheck} color="#3b82f6" bgColor="#dbeafe" />
        <StatCard title="VIP Members" value={stats.vip} icon={Award} color="#8b5cf6" bgColor="#e9d5ff" />
        <StatCard title="Total Spent" value={`₹${stats.totalSpent.toLocaleString()}`} icon={DollarSign} color="#f59e0b" bgColor="#fed7aa" />
      </div>

      {/* Filters */}
      <div className="filters-bar">
        <div className="search-box">
          <Search size={18} />
          <input 
            type="text" 
            placeholder="Search by name, email or ID..." 
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
        <div className="filter-group">
          <select className="filter-select" value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)}>
            <option value="all">All Status</option>
            <option value="Active">Active</option>
            <option value="VIP">VIP</option>
            <option value="Inactive">Inactive</option>
          </select>
          <select className="filter-select" value={filterSort} onChange={(e) => setFilterSort(e.target.value)}>
            <option value="newest">Newest First</option>
            <option value="oldest">Oldest First</option>
            <option value="spent-high">Highest Spent</option>
            <option value="spent-low">Lowest Spent</option>
            <option value="orders-high">Most Orders</option>
          </select>
        </div>
      </div>

      {/* Results Info */}
      <div className="results-info">
        <span>{filteredCustomers.length} Customers Found</span>
      </div>

      {/* Customers Grid */}
      <div className="customers-grid">
        {paginatedCustomers.map((customer) => {
          const cStatus = customer.isActive === false ? 'Inactive' : 'Active';
          const statusConfig = getStatusConfig(cStatus);
          const StatusIcon = statusConfig.icon;
          
          return (
            <div key={customer._id} className="customer-card">
              <div className="customer-card-header">
                <div className="customer-avatar">
                  <span>{customer.username?.charAt(0).toUpperCase()}</span>
                </div>
                <div className="customer-actions">
                  <button className="action-icon" onClick={() => handleViewDetails(customer)} title="View Profile">
                    <Eye size={16} />
                  </button>
                  <button className="action-icon" title="Send Message">
                    <MessageSquare size={16} />
                  </button>
                  <button className="action-icon danger" onClick={() => handleDeleteUser(customer._id)} title="Delete">
                    <Trash2 size={16} />
                  </button>
                </div>
              </div>
              <div className="customer-card-body">
                <h5 className="customer-name">{customer.username}</h5>
                <div className="customer-id">#{customer._id.substring(customer._id.length - 6).toUpperCase()}</div>
                <div className="customer-contact">
                  <div className="contact-item">
                    <Mail size={12} />
                    <span>{customer.email}</span>
                  </div>
                  <div className="contact-item">
                    <Phone size={12} />
                    <span>{customer.phone || 'N/A'}</span>
                  </div>
                </div>
                <div className="customer-stats">
                  <div className="stat">
                    <Calendar size={14} />
                    <span>{new Date(customer.createdAt).toLocaleDateString()}</span>
                  </div>
                </div>
                <div className="customer-status" style={{ backgroundColor: statusConfig.bg, color: statusConfig.color }}>
                  <StatusIcon size={12} />
                  {statusConfig.label}
                </div>
              </div>
              <div className="customer-card-footer">
                <button className="btn-view" onClick={() => handleViewDetails(customer)}>View Details →</button>
              </div>
            </div>
          );
        })}
        {paginatedCustomers.length === 0 && (
          <div className="text-center w-100 py-5 text-muted">No customers found.</div>
        )}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="pagination-wrapper">
          <div className="pagination-info">
            Showing {((currentPage - 1) * itemsPerPage) + 1} to {Math.min(currentPage * itemsPerPage, filteredCustomers.length)} of {filteredCustomers.length} customers
          </div>
          <div className="pagination-buttons">
            <button 
              className={`page-btn ${currentPage === 1 ? 'disabled' : ''}`} 
              onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
              disabled={currentPage === 1}
            >
              <ChevronLeft size={16} /> Previous
            </button>
            {[...Array(totalPages)].map((_, i) => (
              <button 
                key={i}
                className={`page-number ${currentPage === i + 1 ? 'active' : ''}`}
                onClick={() => setCurrentPage(i + 1)}
              >
                {i + 1}
              </button>
            ))}
            <button 
              className={`page-btn ${currentPage === totalPages ? 'disabled' : ''}`} 
              onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
              disabled={currentPage === totalPages}
            >
              Next <ChevronRight size={16} />
            </button>
          </div>
        </div>
      )}

      {/* Customer Details Modal */}
      {showDetailsModal && selectedCustomer && (
        <div className="modal-overlay" onClick={() => setShowDetailsModal(false)}>
          <div className="modal-container" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <div className="modal-header-info">
                <div className="modal-avatar">
                  <span>{selectedCustomer.username?.charAt(0).toUpperCase()}</span>
                </div>
                <div>
                  <h3>{selectedCustomer.username}</h3>
                  <p className="modal-id">#{selectedCustomer._id}</p>
                </div>
              </div>
              <button className="modal-close" onClick={() => setShowDetailsModal(false)}>
                <X size={20} />
              </button>
            </div>

            <div className="modal-body">
              <DetailSection title="Contact Information" icon={User}>
                <div className="info-row">
                  <span className="info-label">Email</span>
                  <span className="info-value">{selectedCustomer.email}</span>
                </div>
                <div className="info-row">
                  <span className="info-label">Phone</span>
                  <span className="info-value">{selectedCustomer.phone || 'N/A'}</span>
                </div>
              </DetailSection>

              <DetailSection title="Account Information" icon={Calendar}>
                <div className="info-row">
                  <span className="info-label">Joined Date</span>
                  <span className="info-value">{new Date(selectedCustomer.createdAt).toLocaleDateString()}</span>
                </div>
                <div className="info-row">
                  <span className="info-label">Status</span>
                  <span className="info-value">
                    <span className="status-badge" style={{ 
                      backgroundColor: getStatusConfig(selectedCustomer.isActive === false ? 'Inactive' : 'Active').bg, 
                      color: getStatusConfig(selectedCustomer.isActive === false ? 'Inactive' : 'Active').color 
                    }}>
                      {selectedCustomer.isActive === false ? 'Inactive' : 'Active'}
                    </span>
                  </span>
                </div>
              </DetailSection>
            </div>

            <div className="modal-footer">
              <button className="btn-secondary" onClick={() => setShowDetailsModal(false)}>Close</button>
              <button className="btn-primary">Send Message</button>
            </div>
          </div>
        </div>
      )}

      
    </div>
  );
};

export default Customers;